import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Package } from "lucide-react";
import useShopStore from "../../store/shopStore";
import { useAuth } from "../../contexts/AuthContext";

/**
 * ProductCard Component - Story-01, Story-02 AC1
 * Displays individual product with image, name, price, and add-to-cart button
 * Design System: WTF Module pin card pattern
 */
const ProductCard = ({ product, onRequestItem, compact = false }) => {
  const navigate = useNavigate();
  const { addToCart } = useShopStore();
  const { user } = useAuth();
  const [isAdding, setIsAdding] = useState(false);
  // An image that can't load (e.g. its storage was removed) shows a placeholder instead of
  // the browser's broken-image icon and alt text.
  const imageSrc = product.primaryImageUrl || product.imageUrl || "/placeholder-product.png";
  const [failedImageSrc, setFailedImageSrc] = useState(null);

  // Check if user is admin, coach, or purchase-manager - they cannot purchase from shop
  const isAdmin = user?.role?.toLowerCase() === "admin";
  const isCoach = user?.role?.toLowerCase() === "coach";
  const isPurchaseManager = user?.role?.toLowerCase() === "purchase-manager";
  const isMedical = user?.role?.toLowerCase() === "medical-incharge";
  
  // Staff Roles (Story 2.2)
  const isStaff = isAdmin || isCoach || isPurchaseManager || isMedical;
  const canPurchase =
    product.inStock && product.isActive !== false && !product.isPendingProduct;
  // Non-essential items show in the shop but children can't buy them.
  const canBuy = canPurchase && product.isEssential !== false;
  const hasDiscountPrice =
    Number(product.discountPrice) > 0;

  // Navigate to product detail page on card click
  const handleCardClick = () => {
    navigate(`/shop/products/${product._id}`);
  };

  const handleCardKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardClick();
    }
  };

  const handleAction = async () => {
    // If staff, request item
    if (isStaff) {
      if (onRequestItem) onRequestItem(product);
      return;
    }

    // If student, add to cart
    if (!canBuy || isAdding) return;

    setIsAdding(true);
    try {
      await addToCart(product, 1);
    } catch (error) {
      // Error toast is handled in the store
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg hover:shadow-lg transition-shadow duration-200 cursor-pointer overflow-hidden flex flex-col h-full"
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="link"
      tabIndex={0}
      aria-label={`View details for ${product.name}`}
    >
      {/* Product Image */}
      <div className={`relative ${compact ? "aspect-[4/3]" : "aspect-square"} flex-shrink-0`}>
        {failedImageSrc === imageSrc ? (
          <div
            role="img"
            aria-label={product.name}
            className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-50 to-slate-100"
          >
            <Package className="w-12 h-12 text-purple-300" aria-hidden="true" />
          </div>
        ) : (
          <img
            src={imageSrc}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => setFailedImageSrc(imageSrc)}
          />
        )}

        {/* Out of Stock Overlay */}
        {!canPurchase && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <span className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold">
              {product.inStock ? "Unavailable" : "Out of Stock"}
            </span>
          </div>
        )}

        {/* Low Stock Badge */}
        {product.lowStock && canPurchase && (
          <div className="absolute top-2 right-2">
            <span className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold">
              Only {product.stock} left!
            </span>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className={`${compact ? "p-3" : "p-4"} flex flex-col flex-1`}>
        {/* Category Badge */}
        <span className="inline-block self-start bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full mb-2">
          {product.category}
        </span>

        {/* Product Name */}
        <h3 className="font-semibold text-slate-900 mb-1 truncate">
          {product.name}
        </h3>

        {/* Description */}
        <p className="text-sm text-slate-600 mb-3 line-clamp-2 min-h-[2.5rem]">
          {product.description}
        </p>

        {/* Spacer to push button to bottom */}
        <div className="flex-1"></div>

        {/* Price */}
        <div className="flex items-center justify-between mb-3">
          {hasDiscountPrice ? (
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-emerald-600">
                {product.discountPrice} coins
              </span>
              <span className="text-sm text-slate-400 line-through">
                {product.price} coins
              </span>
            </div>
          ) : (
            <span className="text-lg font-bold text-slate-900">
              {product.price} coins
            </span>
          )}
        </div>

        {/* Add to Cart / Request Button */}
        <button
          onClick={(e) => { e.stopPropagation(); handleAction(); }}
          disabled={(!canBuy && !isStaff) || isAdding}
          className={`w-full px-4 py-2 rounded-md font-medium flex items-center justify-center gap-2 transition-colors ${
            canBuy || isStaff
              ? "bg-purple-600 text-white hover:bg-purple-700 active:bg-purple-800"
              : "bg-slate-300 text-slate-500 cursor-not-allowed"
          }`}
          aria-label={isStaff ? `Request ${product.name}` : `Add ${product.name} to cart`}
        >
          {isAdding ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Processing...
            </>
          ) : (
            <>
              {isStaff ? <Package className="w-5 h-5" /> : <ShoppingCart className="w-5 h-5" />}
              {isStaff
                ? "Request Item"
                : canBuy
                ? "Add to Cart"
                : canPurchase
                ? "Not for sale"
                : product.inStock
                ? "Unavailable"
                : "Out of Stock"}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
